import { useToast } from '@chakra-ui/react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Configuration, SIdPricingApi, SidPricingOverride } from '../_openapi/pricing';
import { formatDate, SERVER_DATE_FORMAT, today } from '../utils/dateUtils';
import { defaultApiConfig } from './defaultApiConfig';

const config = new Configuration({
    ...defaultApiConfig,
    basePath: '/api/pricing',
});

const pricingApi = new SIdPricingApi(config);

export const usePriceList = () =>
    useQuery({
        queryKey: ['pricing', 'list'],
        queryFn: () => pricingApi.getCachedPrices(),
        refetchInterval: 60000, // 60 sec
        placeholderData: keepPreviousData,
    });

export const useAssetPrice = (sId?: string) =>
    useQuery({
        queryKey: ['pricing', 'asset', sId],
        queryFn: async ({ signal }) => {
            const resp = await pricingApi.price({ requestBody: [sId!] }, { signal });
            return resp?.[0];
        },
        refetchInterval: 60000, // 60 sec
        enabled: !!sId,
    });

export const useAssetCreatePriceOverride = () => {
    const queryClient = useQueryClient();
    const toast = useToast();

    return useMutation({
        mutationFn: async (override: SidPricingOverride) => {
            return pricingApi.override({
                sidPricingOverride: [
                    {
                        sId: override.sId,
                        price: override.price,
                        date: `${formatDate(today(), SERVER_DATE_FORMAT)}Z`,
                    },
                ],
            });
        },
        onSuccess: (response, request) => {
            queryClient.invalidateQueries({ queryKey: ['pricing', 'asset', request.sId] });
            queryClient.invalidateQueries({ queryKey: ['pricing', 'list'] });
            toast({
                title: 'Price Override successful',
                description: 'Price override saved successfully',
                status: 'success',
                duration: 3000,
            });
        },
        onError: (error: Error) => {
            toast({
                title: 'Failed to save Price Override',
                description: error.message,
                status: 'error',
                duration: null,
            });
        },
    });
};

export const useAssetClearPriceOverride = () => {
    const queryClient = useQueryClient();
    const toast = useToast();

    return useMutation({
        mutationFn: async (sId: string) => {
            return pricingApi.clearOverrides({
                requestBody: [sId],
            });
        },
        onSuccess: (response, request) => {
            queryClient.invalidateQueries({ queryKey: ['pricing', 'asset', request] });
            queryClient.invalidateQueries({ queryKey: ['pricing', 'list'] });
            toast({
                title: 'Price Override cleared',
                description: 'Price override removed successfully',
                status: 'success',
                duration: 3000,
            });
        },
        onError: (error: Error) => {
            toast({
                title: 'Failed to remove Price Override',
                description: error.message,
                status: 'error',
                duration: null,
            });
        },
    });
};

export const useForceUpdateSidPricers = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async () => pricingApi.forceUpdateSidPricers(),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['pricing', 'asset'] });
            queryClient.invalidateQueries({ queryKey: ['pricing', 'list'] });
        },
    });
};
